import { useIntl } from 'react-intl'
import { Controller, useForm } from 'react-hook-form'
import { NavLink, useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { toast } from 'react-toastify'
import { CheckCircle, XCircle } from 'phosphor-react'

import { api } from '../../services/api'
import { Button } from '../../components/Button'
import { PasswordInput } from '../../components/FormElements/PasswordInput'
import { createResetPasswordSchema } from '../../validators/resetPasswordSchema'
import { useAuth } from '../../contexts/AuthContext'

type FormValues = {
  password: string
  passwordConfirmation: string
}

const schema = createResetPasswordSchema({
  password: {
    min: 'INVALID_PASSWORD',
    matches: 'INVALID_PASSWORD',
    identical: 'PASSWORDS_MUST_BE_IDENTICAL',
    required: 'REQUIRED_FIELD',
  },
  passwordConfirmation: {
    identical: 'PASSWORDS_MUST_BE_IDENTICAL',
    required: 'REQUIRED_FIELD',
  },
})

export const ChangeExpertPassword = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { formatMessage } = useIntl()

  const { control, formState, handleSubmit } = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const onSubmit = async (values: FormValues) => {
    try {
      const response = await api.put(`/experts/update-password/${user?.id}`, {
        ...values,
      })

      if (response.status === 200) {
        toast(
          formatMessage({
            id: 'PASSWORD_SUCCESSFULLY_RESET',
          }),
          {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            closeButton: false,
            icon: (
              <CheckCircle
                size={48}
                className="mx-auto my-auto text-alert-success"
              />
            ),
            className: 'bg-green-50',
          }
        )

        navigate('/expert/meetings')
      }
    } catch (error) {
      toast(
        formatMessage({
          id: 'AN_ERROR_OCCURRED_WHILE_TRYING_TO_UPDATE_PASSWORD',
        }),
        {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          closeButton: false,
          icon: (
            <XCircle size={48} className="mx-auto my-auto text-alert-error" />
          ),
          className: 'bg-red-50',
        }
      )
    }
  }

  return (
    <div className="w-full lg:px-52">
      <header className="mb-14">
        <h2 className="text-lg font-medium text-grey-800">
          {formatMessage({ id: 'REGISTER_NEW_PASSWORD' })}
        </h2>
      </header>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between w-full h-full max-w-[402px] grow "
      >
        <section className="mb-14">
          <div className="mb-4">
            <Controller
              name="password"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { invalid, error },
              }) => (
                <PasswordInput
                  autoFocus
                  isValid={!invalid}
                  label={formatMessage({ id: 'ENTER_YOUR_NEW_PASSWORD' })}
                  autoComplete="new-password"
                  value={value}
                  onChange={onChange}
                  errorMessage={
                    error?.message && formatMessage({ id: error.message })
                  }
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="passwordConfirmation"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { invalid, error },
              }) => (
                <PasswordInput
                  isValid={!invalid}
                  label={formatMessage({ id: 'TYPE_PASSWORD_AGAIN' })}
                  autoComplete="new-password"
                  value={value}
                  onChange={onChange}
                  errorMessage={
                    error?.message && formatMessage({ id: error.message })
                  }
                />
              )}
            />
          </div>
        </section>

        <footer className="flex flex-col flex-wrap gap-2 md:flex-row-reverse">
          <div className="flex-1">
            <Button
              disabled={!formState.isValid}
              isLoading={formState.isSubmitting}
              type="submit"
              className="whitespace-nowrap"
              fullWidth
            >
              {formatMessage({ id: 'REGISTER_NEW_PASSWORD' })}
            </Button>
          </div>

          <div className="flex-1">
            <NavLink to="/expert/meetings">
              <Button
                disabled={formState.isSubmitting}
                type="button"
                variant="outline"
                fullWidth
              >
                {formatMessage({ id: 'CANCEL' })}
              </Button>
            </NavLink>
          </div>
        </footer>
      </form>
    </div>
  )
}
